/** @jsxImportSource theme-ui */
import { jsx } from 'theme-ui';
import styled from '@emotion/styled';
import { GatsbyImage } from 'gatsby-plugin-image';
import { SvgBackground } from '../../backgrounds';
import { Container } from '../';
import { StyledHeading, WrapperContainer } from '../../modules/utils';
import { Flex, Heading, Text, Box } from '@theme-ui/components';

const BannerText = ({
  title,
  subtitle,
  description,
  headerContentWidth,
  textAlignment,
}) => (
  <Box
    sx={{
      maxWidth: ['95%', '70%', headerContentWidth],
      marginRight: 1,
      marginLeft: [0, 0, 5, 0],
      mt: [5, 0],
      border: '0px solid red',
      textAlign: ['center', 'left'],
      alignSelf: textAlignment,
    }}>
    <Heading
      id="hero_h1"
      variant="newmoduleH1"
      sx={{ marginTop: [4, 4, 4, 1], marginBottom: 1 }}
      as="h1">
      {title}
    </Heading>
    {subtitle ? (
      <Heading id="hero_h2" variant="newmoduleH2" as="h2">
        {subtitle}
      </Heading>
    ) : null}
    <StyledBannerDescription>
      <div dangerouslySetInnerHTML={{ __html: description }} />
    </StyledBannerDescription>
  </Box>
);

const ModuleBanner = ({
  image,
  alt,
  title,
  subtitle,
  description,
  headerContentWidth,
  bannerTextAlignment,
  bannerImageMarginTop = [0, 0, 3, 4],
  height = [570, 556, 786, 588],
  backgroundColor,
  backgroundSvgName,
}) => {
  const isBannerTextLeft = bannerTextAlignment === 'left';
  const imageMarginL = isBannerTextLeft ? 0 : [3, 3, 0, 0];
  const imageMarginR = isBannerTextLeft ? [3, 3, 3, 0] : 0;
  const flexDirection = isBannerTextLeft
    ? ['column-reverse', 'column-reverse', 'column-reverse', 'row']
    : ['column-reverse', 'column-reverse', 'column-reverse', 'row-reverse'];
  const imageAlignment = isBannerTextLeft
    ? ['flex-end', 'flex-end', 'flex-end', 'flex-start']
    : 'flex-start';
  const textAlignment = isBannerTextLeft
    ? ['center', 'flex-start', 'flex-start', 'center']
    : ['center', 'flex-end', 'flex-end', 'center'];

  return (
    <WrapperContainer
      backgroundSvgName={backgroundSvgName}
      backgroundColor={backgroundColor} id='product-hero'>
      <Box sx={{ height, border: '0px solid green' }}>
        <Flex
          sx={{
            flexDirection,
            justifyContent: [
              'flex-end',
              'space-around',
              'space-around',
              'space-between',
            ],
            alignItems: 'center',
            height: '100%',
            'a': {
              textDecoration: 'none',
              color: 'primary',
              '&:hover': {
                color: 'primary',
              }
            }
          }}>
          <BannerText
            title={title}
            subtitle={subtitle}
            description={description}
            headerContentWidth={headerContentWidth}
            textAlignment={textAlignment}
          />
          {image && (isBannerTextLeft ? (
            <RightAlignedImgWrapper
              sx={{
                mt: bannerImageMarginTop,
                mr: imageMarginR,
                ml: imageMarginL,
                width: ['55%', '30%', '34%', '36%'],
                alignSelf: imageAlignment,
              }}>
              <GatsbyImage image={image} alt={alt} />
            </RightAlignedImgWrapper>
          ) : (
            <LeftAlignedImgWrapper
              sx={{
                mt: bannerImageMarginTop,
                mr: imageMarginR,
                ml: imageMarginL,
                width: ['55%', '30%', '34%', '36%'],
                alignSelf: imageAlignment,
              }}>
              <GatsbyImage image={image} alt={alt} />
            </LeftAlignedImgWrapper>
          ))}
        </Flex>
      </Box>
    </WrapperContainer>
  );
};

const bannerStyle = {
  flexDirection: ['column', 'row'],
  justifyContent: ['center', 'space-between'],
  alignItems: 'center',
  height: ['auto', '525px'],
};

const StyledBannerDescription = styled(Text)`
	span,
	p {
		font-size: 1.25rem;
		font-weight: 400 !important;
		line-height: 34px;
		max-width: 100%;
    margin: 20px 0;
	}
	@media (max-width: 768px) {
		span,
		p {
			font-size: 1rem;
			line-height: 1.625rem;
			max-width: 100%;
			font-weight: 400 !important;
		}
	}
`;

const LeftAlignedImgWrapper = styled(Box)`
  @media (min-width: 1280px) {
    position: relative;
    left: -4%;
  }
  @media (min-width: 1400px) {
    position: relative;
    left: -10%;
  }
  @media (min-width: 1800px) {
    position: relative;
    left: -15%;
  }
  @media (min-width: 1980px) {
    position: relative;
    left: -20%;
  }
  @media (min-width: 2020px) {
    position: relative;
    left: -12%;
  }
  @media (min-width: 2160px) {
    position: relative;
    left: -15%;
  }
  @media (min-width: 2330px) {
    position: relative;
    left: -17%;
  }
`;

const RightAlignedImgWrapper = styled(Box)`
  @media (min-width: 1280px) {
    position: relative;
    right: -4%;
  }
  @media (min-width: 1400px) {
    position: relative;
    right: -10%;
  }
  @media (min-width: 1600px) {
    position: relative;
    right: -10%;
  }
  @media (min-width: 1800px) {
    position: relative;
    right: -15%;
  }
  @media (min-width: 1980px) {
    position: relative;
    right: -20%;
  }
  @media (min-width: 2020px) {
    position: relative;
    right: -12%;
  }
  @media (min-width: 2160px) {
    position: relative;
    right: -15%;
  }
  @media (min-width: 2330px) {
    position: relative;
    right: -17%;
  }
`;

export default ModuleBanner;
